import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, formatApiError } from "../api/client";
import { ErrorState, LoadingState } from "../components/AsyncState";
import { Field, PageHeader } from "../components/PageHeader";

type Setting = {
  key: string;
  value_json: string;
  value_type: "string" | "number" | "boolean";
  is_public: number;
  updated_at: string;
};

export function SettingsPage() {
  const query = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api<Setting[]>("/settings")).data
  });
  return (
    <div className="page">
      <PageHeader title="إعدادات النظام" description="تعديل القيم التشغيلية بسبب موثق يظهر في سجل التدقيق." />
      {query.isPending ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState error={query.error} retry={() => void query.refetch()} />
      ) : (
        <section className="card-list">
          {query.data.map((item) => (
            <SettingCard item={item} key={item.key} />
          ))}
        </section>
      )}
    </div>
  );
}

function SettingCard({ item }: { item: Setting }) {
  const queryClient = useQueryClient();
  const parsed = JSON.parse(item.value_json) as string | number | boolean;
  const [value, setValue] = useState(String(parsed));
  const [reason, setReason] = useState("");
  const [saved, setSaved] = useState(false);
  const update = useMutation({
    mutationFn: () =>
      api(`/settings/${encodeURIComponent(item.key)}`, {
        method: "PUT",
        body: { value: castValue(value, item.value_type), reason }
      }),
    onSuccess: async () => {
      setSaved(true);
      setReason("");
      await queryClient.invalidateQueries({ queryKey: ["settings"] });
    }
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaved(false);
    update.mutate();
  }
  return (
    <article className="content-card">
      <div className="section-head">
        <div>
          <h2 dir="ltr">{item.key}</h2>
          <p>
            {item.is_public ? "إعداد عام" : "إعداد داخلي"} · النوع {item.value_type}
          </p>
        </div>
      </div>
      <form className="form-grid" onSubmit={submit}>
        <Field label="القيمة" htmlFor={`setting-${item.key}`}>
          {item.value_type === "boolean" ? (
            <select id={`setting-${item.key}`} value={value} onChange={(e) => setValue(e.target.value)}>
              <option value="true">نعم</option>
              <option value="false">لا</option>
            </select>
          ) : (
            <input
              id={`setting-${item.key}`}
              type={item.value_type === "number" ? "number" : "text"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
            />
          )}
        </Field>
        <Field label="سبب التغيير" htmlFor={`setting-reason-${item.key}`}>
          <input
            id={`setting-reason-${item.key}`}
            minLength={5}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />
        </Field>
        {update.isError ? (
          <div className="inline-alert form-span" role="alert">
            {formatApiError(update.error)}
          </div>
        ) : null}
        {saved ? (
          <p className="success-text form-span" role="status">
            تم حفظ الإعداد وتوثيق السبب.
          </p>
        ) : null}
        <button className="button form-span" type="submit" disabled={update.isPending}>
          <Save aria-hidden="true" /> {update.isPending ? "جارٍ الحفظ…" : "حفظ الإعداد"}
        </button>
      </form>
    </article>
  );
}

function castValue(value: string, type: Setting["value_type"]): string | number | boolean {
  if (type === "boolean") return value === "true";
  if (type === "number") return Number(value);
  return value;
}
